import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import { Search, X, Boxes, MapPin, User, Plus, HardDrive, CheckCircle2, Wrench } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';

const StyledCard = styled(Card)({
  boxShadow:
    '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
});

const statusColor = (status) => {
  switch (status) {
    case 'DISPONIBILE':
      return 'success';
    case 'ASSEGNATO':
      return 'primary';
    case 'IN_MANUTENZIONE':
      return 'warning';
    case 'DISMESSO':
      return 'default';
    case 'PERSO':
      return 'error';
    default:
      return 'default';
  }
};

const usageLabel = (usageType) => {
  switch (usageType) {
    case 'SEDE':
      return 'Uso in sede';
    case 'SMART_WORKING':
      return 'Smart working';
    case 'TRASFERTA':
      return 'Trasferta';
    case 'MISTO':
      return 'Misto';
    default:
      return '';
  }
};

const StatWidget = ({ title, value, subtitle, icon: Icon, color, onClick, active }) => (
  <Paper
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      borderWidth: active ? 2 : 1,
      borderStyle: 'solid',
      borderColor: active ? color : 'rgba(145, 158, 171, 0.24)',
      boxShadow: active
        ? '0 0 2px 0 rgba(145, 158, 171, 0.3), 0 16px 32px -4px rgba(145, 158, 171, 0.28)'
        : '0 0 2px 0 rgba(145, 158, 171, 0.16), 0 12px 24px -4px rgba(145, 158, 171, 0.08)',
      transition: 'box-shadow 0.2s, transform 0.1s, border-color 0.2s',
      '&:hover': onClick
        ? {
            boxShadow:
              '0 0 2px 0 rgba(145, 158, 171, 0.3), 0 16px 32px -4px rgba(145, 158, 171, 0.28)',
            transform: 'translateY(-1px)'
          }
        : undefined
    }}
    elevation={0}
    variant="outlined"
    onClick={onClick}
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon size={24} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
  </Paper>
);

export default function Assets() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [assignmentTypeFilter, setAssignmentTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [officeFilter, setOfficeFilter] = useState('');

  const [categories, setCategories] = useState([]);
  const [offices, setOffices] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [kpi, setKpi] = useState(null);
  const [activeStatusChip, setActiveStatusChip] = useState('ALL');

  const fetchFilters = async () => {
    setFiltersLoading(true);
    try {
      const [catRes, officeRes, kpiRes] = await Promise.all([
        api.get('/asset-categories'),
        api.get('/office'),
        api.get('/assets/kpi')
      ]);
      setCategories(catRes.data || []);
      setOffices(officeRes.data || []);
      setKpi(kpiRes.data || null);
    } catch (error) {
      console.error('Error loading asset filters', error);
    } finally {
      setFiltersLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage
      };
      const trimmed = search.trim();
      if (trimmed) {
        params.q = trimmed;
      }
      if (statusFilter) {
        params.status = statusFilter;
      }
      if (assignmentTypeFilter) {
        params.assignment_type = assignmentTypeFilter;
      }
      if (categoryFilter) {
        params.category_id = categoryFilter;
      }
      if (officeFilter) {
        params.office_id = officeFilter;
      }
      const res = await api.get('/assets', { params });
      const payload = res.data || {};
      setRows(payload.data || []);
      setTotal(payload.total || 0);
    } catch (error) {
      console.error('Error loading assets', error);
      toast.error('Errore nel caricamento degli asset');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, search, statusFilter, assignmentTypeFilter, categoryFilter, officeFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (row) => {
    navigate(`/assets/${row.id}`);
  };

  const renderHolder = (row) => {
    if (!row.current_holder_user_id) {
      return (
        <Typography variant="body2" color="text.secondary">
          Non assegnato
        </Typography>
      );
    }
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: '#eef2ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <User size={16} color="#4f46e5" />
        </Box>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {row.holder_name || `ID ${row.current_holder_user_id}`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.holder_email || ''}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: '#eef2ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Boxes size={20} color="#4f46e5" />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                Asset aziendali
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Gestione del censimento e delle assegnazioni degli asset aziendali
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate('/assets/new')}
          >
            <Plus size={16} style={{ marginRight: 8 }} />
            Nuovo asset
          </Button>
        </Box>
        {kpi && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <StatWidget
                title="Asset totali"
                value={kpi.summary?.total_assets || 0}
                subtitle="Tutti gli asset attivi"
                icon={HardDrive}
                color="#111827"
                active={activeStatusChip === 'ALL'}
                onClick={() => {
                  setStatusFilter('');
                  setAssignmentTypeFilter('');
                  setActiveStatusChip('ALL');
                  setPage(0);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatWidget
                title="Disponibili"
                value={kpi.summary?.available_assets || 0}
                subtitle="Pronti per assegnazione"
                icon={CheckCircle2}
                color="#16a34a"
                active={activeStatusChip === 'DISPONIBILE'}
                onClick={() => {
                  setStatusFilter('DISPONIBILE');
                  setAssignmentTypeFilter('');
                  setActiveStatusChip('DISPONIBILE');
                  setPage(0);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatWidget
                title="Assegnati"
                value={kpi.summary?.assigned_assets || 0}
                subtitle="In uso al personale"
                icon={User}
                color="#0f172a"
                active={activeStatusChip === 'ASSEGNATO'}
                onClick={() => {
                  setStatusFilter('ASSEGNATO');
                  setAssignmentTypeFilter('');
                  setActiveStatusChip('ASSEGNATO');
                  setPage(0);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatWidget
                title="In manutenzione"
                value={kpi.summary?.maintenance_assets || 0}
                subtitle="Temporaneamente non disponibili"
                icon={Wrench}
                color="#eab308"
                active={activeStatusChip === 'IN_MANUTENZIONE'}
                onClick={() => {
                  setStatusFilter('IN_MANUTENZIONE');
                  setAssignmentTypeFilter('');
                  setActiveStatusChip('IN_MANUTENZIONE');
                  setPage(0);
                }}
              />
            </Grid>
          </Grid>
        )}
      </Box>

      <StyledCard>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: 1, minWidth: 260 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Cerca per codice, descrizione, modello o seriale..."
                value={search}
                onChange={(event) => {
                  setPage(0);
                  setSearch(event.target.value);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} color="#637381" />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setPage(0);
                          setSearch('');
                        }}
                      >
                        <X size={16} />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }}
              />
            </Box>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Stato</InputLabel>
              <Select
                label="Stato"
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value);
                  setActiveStatusChip('CUSTOM');
                  setPage(0);
                }}
              >
                <MenuItem value="">
                  <em>Tutti</em>
                </MenuItem>
                <MenuItem value="DISPONIBILE">Disponibile</MenuItem>
                <MenuItem value="ASSEGNATO">Assegnato</MenuItem>
                <MenuItem value="IN_MANUTENZIONE">In manutenzione</MenuItem>
                <MenuItem value="PERSO">Perso</MenuItem>
                <MenuItem value="DISMESSO">Dismesso</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Tipo utilizzo</InputLabel>
              <Select
                label="Tipo utilizzo"
                value={assignmentTypeFilter}
                onChange={(event) => {
                  setAssignmentTypeFilter(event.target.value);
                  setActiveStatusChip('CUSTOM');
                  setPage(0);
                }}
              >
                <MenuItem value="">
                  <em>Tutti</em>
                </MenuItem>
                <MenuItem value="SEDE">Uso in sede</MenuItem>
                <MenuItem value="SMART_WORKING">Smart working</MenuItem>
                <MenuItem value="TRASFERTA">Trasferta</MenuItem>
                <MenuItem value="MISTO">Misto</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }} disabled={filtersLoading}>
              <InputLabel>Categoria</InputLabel>
              <Select
                label="Categoria"
                value={categoryFilter}
                onChange={(event) => {
                  setCategoryFilter(event.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">
                  <em>Tutte</em>
                </MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }} disabled={filtersLoading}>
              <InputLabel>Sede</InputLabel>
              <Select
                label="Sede"
                value={officeFilter}
                onChange={(event) => {
                  setOfficeFilter(event.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">
                  <em>Tutte</em>
                </MenuItem>
                {offices.map((office) => (
                  <MenuItem key={office.id} value={office.id}>
                    {office.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Codice</TableCell>
                <TableCell>Descrizione</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MapPin size={14} />
                    <span>Sede</span>
                  </Box>
                </TableCell>
                <TableCell>Stato</TableCell>
                <TableCell>Assegnatario</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Nessun asset trovato con i filtri correnti.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row)}
                  >
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, fontFamily: 'monospace' }}
                      >
                        {row.asset_code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {row.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.model || row.serial_number || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={
                          row.category_default_usage_type
                            ? `Uso predefinito: ${usageLabel(row.category_default_usage_type)}`
                            : ''
                        }
                        arrow
                        disableInteractive
                      >
                        <Box>
                          <Typography variant="body2">
                            {row.category_name || row.category_code || '-'}
                          </Typography>
                          {row.category_default_usage_type && (
                            <Typography variant="caption" color="text.secondary">
                              {usageLabel(row.category_default_usage_type)}
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            bgcolor: '#ecfeff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <MapPin size={14} color="#0f766e" />
                        </Box>
                        <Typography variant="body2">
                          {row.office_name || '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.current_status}
                        color={statusColor(row.current_status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{renderHolder(row)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>
    </Box>
  );
}
